import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ConfirmDialog,
  Icon,
  PageHeader,
} from '../../components';
import { ApiError, apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import { LeadFormModal } from './LeadFormModal';
import { CommentsTab } from './tabs/CommentsTab';
import { FollowupsTab } from './tabs/FollowupsTab';
import { AttachmentsTab } from './tabs/AttachmentsTab';
import { HistoryTab } from './tabs/HistoryTab';
import type { LeadRow, LeadUpdatePayload, LookupOption, UserOption } from './types';

type Tab = 'overview' | 'comments' | 'followups' | 'attachments' | 'history';

type ClientRow = {
  id: string;
  full_name: string | null;
  email: string | null;
};

export function LeadDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  const [converting, setConverting] = useState(false);

  const leadQ = useQuery({
    queryKey: ['lead', id],
    queryFn: () => apiRequest<{ data: LeadRow }>(`/leads/${id}`),
    enabled: !!id,
  });

  const statusesQ = useQuery({
    queryKey: ['lookup', 'lead-statuses'],
    queryFn: () =>
      apiRequest<ListResponse<LookupOption>>(
        '/settings/lead-statuses?limit=100&include_inactive=true',
      ),
  });
  const categoriesQ = useQuery({
    queryKey: ['lookup', 'lead-categories'],
    queryFn: () =>
      apiRequest<ListResponse<LookupOption>>(
        '/settings/lead-categories?limit=100&include_inactive=true',
      ),
  });
  const sourcesQ = useQuery({
    queryKey: ['lookup', 'sources'],
    queryFn: () =>
      apiRequest<ListResponse<LookupOption>>(
        '/settings/sources?limit=100&include_inactive=true',
      ),
  });
  const usersQ = useQuery({
    queryKey: ['lookup', 'users'],
    queryFn: () =>
      apiRequest<ListResponse<UserOption>>('/users?limit=200&include_inactive=false'),
  });

  const updateMut = useMutation({
    mutationFn: (body: LeadUpdatePayload) =>
      apiRequest<{ data: LeadRow }>(`/leads/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', id] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
  const convertMut = useMutation({
    mutationFn: () =>
      apiRequest<{ data: { lead: LeadRow; client: ClientRow } }>(`/leads/${id}/convert`, {
        method: 'POST',
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['lead', id] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      navigate(`/clients/${res.data.client.id}`);
    },
  });

  if (leadQ.isLoading) return <div className="card card-body muted">Loading…</div>;
  if (leadQ.error) {
    return (
      <div className="alert">
        {leadQ.error instanceof Error ? leadQ.error.message : 'Failed to load lead'}
      </div>
    );
  }
  const lead = leadQ.data?.data;
  if (!lead) return null;

  const statuses = statusesQ.data?.data ?? [];
  const categories = categoriesQ.data?.data ?? [];
  const sources = sourcesQ.data?.data ?? [];
  const users = usersQ.data?.data ?? [];

  const status = statuses.find((s) => s.id === lead.lead_status_id);
  const category = categories.find((c) => c.id === lead.lead_category_id);
  const source = sources.find((s) => s.id === lead.source_id);
  const sales = users.find((u) => u.id === lead.assigned_sales_user_id);
  const pse = users.find((u) => u.id === lead.assigned_pse_user_id);

  const convertedOn = lead.converted_at ? formatDate(lead.converted_at) : null;
  const carryOver = (
    [
      ['Name', lead.full_name],
      ['Email', lead.email],
      ['Phone', lead.phone],
      ['Company', lead.company],
      ['Country', lead.country],
      ['Date of birth', lead.date_of_birth],
      ['Assigned sales', sales?.full_name ?? null],
      ['Assigned PSE', pse?.full_name ?? null],
    ] as Array<[string, string | null | undefined]>
  ).filter((entry): entry is [string, string] => Boolean(entry[1]));

  return (
    <>
      <PageHeader
        crumb={<Link to="/leads">Leads</Link>}
        title={lead.full_name ?? 'Unnamed lead'}
        subtitle={lead.email ?? lead.company ?? '—'}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {lead.is_converted
              ? lead.converted_client_id && (
                  <Button
                    variant="primary"
                    icon="arrowRight"
                    onClick={() => navigate(`/clients/${lead.converted_client_id}`)}
                  >
                    View client
                  </Button>
                )
              : (
                <Button variant="primary" icon="convert" onClick={() => setConverting(true)}>
                  Convert to client
                </Button>
              )}
            <Button icon="edit" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
        }
      />

      {lead.is_converted && (
        <div
          className="card"
          style={{ marginBottom: 12, borderColor: 'var(--green-300)', background: 'var(--green-50)' }}
        >
          <div
            className="card-body"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'var(--green-600)',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <Icon name="check" size={16} />
              </span>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--green-900)' }}>
                  Converted to client{convertedOn ? ` on ${convertedOn}` : ''}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {lead.converted_client_id
                    ? 'Contact details, company, country and assignments were carried over to the client record.'
                    : 'This lead is marked as converted but is not linked to a client record.'}
                </div>
              </div>
            </div>
            {lead.converted_client_id ? (
              <Button
                icon="arrowRight"
                onClick={() => navigate(`/clients/${lead.converted_client_id}`)}
              >
                View client
              </Button>
            ) : (
              <span className="badge warn">Linked client missing</span>
            )}
          </div>
        </div>
      )}

      <div className="tabs" style={{ marginBottom: 12 }}>
        {(
          [
            ['overview', 'Overview'],
            ['comments', 'Comments'],
            ['followups', 'Follow-ups'],
            ['attachments', 'Attachments'],
            ['history', 'History'],
          ] as Array<[Tab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card">
          <div
            className="card-body"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
          >
            <Field label="Full name" value={lead.full_name} />
            <Field label="Email" value={lead.email} />
            <Field label="Phone" value={lead.phone} />
            <Field label="Company" value={lead.company} />
            <Field label="Country" value={lead.country} />
            <Field label="LinkedIn" value={lead.linkedin_url} />
            <Field label="Date of birth" value={lead.date_of_birth} />
            <Field label="Entry date" value={lead.entry_date} />
            <Field label="Status" value={status?.name ?? null} />
            <Field label="Category" value={category?.name ?? null} />
            <Field label="Source" value={source?.name ?? null} />
            <Field label="Received reference" value={lead.received_reference} />
            <Field label="Assigned sales" value={sales?.full_name ?? null} />
            <Field label="Assigned PSE (pre-sales person)" value={pse?.full_name ?? null} />
            <Field
              label="Notes"
              value={lead.notes}
              style={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap' }}
            />
          </div>
        </div>
      )}

      {tab === 'comments' && <CommentsTab leadId={lead.id} />}
      {tab === 'followups' && <FollowupsTab leadId={lead.id} users={users} />}
      {tab === 'attachments' && <AttachmentsTab leadId={lead.id} />}
      {tab === 'history' && <HistoryTab entityType="lead" entityId={lead.id} />}

      {editing && (
        <LeadFormModal
          row={lead}
          statuses={statuses}
          categories={categories}
          sources={sources}
          users={users}
          onClose={() => setEditing(false)}
          onSubmit={async (payload) => {
            await updateMut.mutateAsync(payload);
            setEditing(false);
          }}
        />
      )}

      <ConfirmDialog
        open={converting}
        title="Convert lead to client"
        message={
          <>
            <p style={{ margin: '0 0 12px' }}>
              Convert <strong>{lead.full_name ?? 'this lead'}</strong> into a new client? The lead
              will be marked as converted and linked to the new client record.
            </p>
            {carryOver.length > 0 && (
              <>
                <div className="field-label" style={{ marginBottom: 6 }}>
                  Carried over to the client
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
                  {carryOver.map(([label, value]) => (
                    <li key={label}>
                      {label}: <strong>{value}</strong>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        }
        confirmLabel="Convert"
        loading={convertMut.isPending}
        onCancel={() => setConverting(false)}
        onConfirm={async () => {
          try {
            await convertMut.mutateAsync();
            setConverting(false);
          } catch (err) {
            alert(err instanceof ApiError ? err.message : 'Conversion failed');
          }
        }}
      />
    </>
  );
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function Field({
  label,
  value,
  style,
}: {
  label: string;
  value: string | null | undefined;
  style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      <div className="field-label">{label}</div>
      <div>{value || <span className="muted">—</span>}</div>
    </div>
  );
}
